import React, { useEffect, useId, useState } from 'react';
import Select, { components } from 'react-select';
import api from '../services/apiService';

export const fiscalTaxLocked = document => Boolean(document.taxLockedAt || document.invoiceNumber
  || document.creditNoteNumber || (document.id && document.status && document.status !== 'draft'));

function TaxOption(props) {
  return (
    <components.Option {...props}>
      <input type="checkbox" checked={props.isSelected} readOnly tabIndex={-1} aria-hidden="true"
        style={{ width: 16, height: 16, margin: 0, padding: 0, flexShrink: 0, pointerEvents: 'none' }} />
      <span>{props.children}</span>
    </components.Option>
  );
}

function TaxSingleValue(props) {
  return (
    <components.SingleValue {...props}>
      {props.selectProps['aria-invalid'] && <span aria-hidden="true">⚠ </span>}
      Steuerbehandlung: {props.children}
    </components.SingleValue>
  );
}

const selectComponents = { Option: TaxOption, SingleValue: TaxSingleValue };
const selectStyles = {
  container: base => ({ ...base, width: 'max-content', maxWidth: '100%', minWidth: 0 }),
  control: base => ({
    ...base, minHeight: 36,
    // Keep the hidden focus input independent of the global .col input styles.
    'input': { height: 0, padding: 0, margin: 0, border: 0 },
  }),
  singleValue: base => ({ ...base, color: '#333' }),
  option: base => ({ ...base, display: 'flex', alignItems: 'center', gap: 8 }),
  menuPortal: base => ({ ...base, zIndex: 9999 }),
};
const visuallyHidden = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0,
};

export default function TaxTreatmentFields({ document, onChange, customer, readOnly = false }) {
  const id = useId();
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const [loadedCustomer, setLoadedCustomer] = useState(null);
  useEffect(() => {
    let active = true;
    api.get('/tax/options').then(({ data }) => { if (active) setOptions(data); })
      .catch(() => { if (active) setError('Die Steueroptionen konnten nicht geladen werden. Bitte die Seite neu laden.'); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    setLoadedCustomer(null);
    if (document.customerId && (!customer || customer.country === undefined || customer.vatId === undefined)) {
      api.get('/customers/' + document.customerId).then(({ data }) => {
        if (active) setLoadedCustomer(data);
      }).catch(() => { /* The server validates the country when saving. */ });
    }
    return () => { active = false; };
  }, [document.customerId, customer]);
  const party = document.taxPartySnapshot?.customer || loadedCustomer || customer || {};
  const domestic = ['DE', 'GERMANY', 'DEUTSCHLAND'].includes(String(party.country || '').trim().toUpperCase());
  const foreign = !!party.country && !domestic;
  const treatment = document.taxTreatment || '';
  const withoutVat = treatment && treatment !== 'standard';
  const legacyLabel = 'Bisherige Steuerbehandlung (' + (document.vatRate ?? 19) + ' %)';
  const treatments = (options?.treatments || [])
    .filter(option => option.value === 'standard' || foreign || option.value === treatment);
  const selectedOption = treatments.find(option => option.value === treatment) || {
    value: treatment,
    label: !treatment && document.id ? legacyLabel
      : withoutVat ? 'Ohne deutsche Umsatzsteuer' : 'Mit deutscher Umsatzsteuer',
  };
  const warnings = [];
  if (!readOnly && withoutVat && !foreign) {
    warnings.push('Bitte das Kundenland prüfen. Die ausgewählte Auslandsabrechnung passt nicht zu einem fehlenden oder deutschen Kundenland.');
  }
  if (!readOnly && ['eu_reverse_charge', 'intra_community_supply'].includes(treatment) && !party.vatId) {
    warnings.push('Vor der Rechnungserstellung muss die USt-ID des Kunden hinterlegt sein.');
  }
  if (error) warnings.push(error);
  const description = [
    selectedOption.label,
    'Steuersatz: ' + (document.vatRate ?? options?.defaultVatRate ?? 19) + ' %.',
    document.taxNote,
    readOnly
      ? 'Die Steuerbehandlung wird vom Ursprungsbeleg übernommen und ist hier nicht änderbar.'
      : 'Die Auswahl gilt für alle Positionen.',
    ...warnings,
  ].filter(Boolean).join(' ');
  const changeTreatment = selected => {
    if (readOnly || !selected || selected.value === treatment) return;
    const profile = options?.treatments.find(option => option.value === selected.value);
    if (!profile) return;
    onChange(previous => ({
      ...previous, taxTreatment: profile.value,
      vatRate: profile.value === 'standard' ? options.defaultVatRate : 0,
      taxNote: profile.note,
    }));
  };
  return (
    <div style={{ margin: '8px auto 16px', width: 'max-content', maxWidth: '100%', minWidth: 0 }} title={description}>
      <Select inputId={id} instanceId={id} aria-label="Steuerbehandlung"
        aria-describedby={id + '-description'} aria-invalid={warnings.length > 0}
        value={selectedOption} options={treatments} onChange={changeTreatment}
        isDisabled={readOnly || !options} isLoading={!options && !error}
        isSearchable={false} isClearable={false} isMulti={false}
        closeMenuOnSelect hideSelectedOptions={false}
        components={selectComponents} styles={selectStyles}
        menuPortalTarget={window.document.body} menuPosition="fixed" />
      <span id={id + '-description'} style={visuallyHidden} role={warnings.length ? 'alert' : undefined}>
        {description}
      </span>
    </div>
  );
}
